import { datadogRum } from "@datadog/browser-rum";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { useActivityClose } from "PFCore/hooks/queries/activity/use_activity_close";
import { useAdminTargetAllocationInvalidate } from "PFCore/hooks/queries/admin/target_allocations/use_target_allocation_invalidate";
import { useShortlistReset } from "PFCore/hooks/queries/shortlists/use_shortlist_actions";
import { useTargetAllocationInvalidate } from "PFCore/hooks/queries/target_allocation/use_target_allocation_invalidate";
import { ClosureReasonKey } from "PFCore/types";
import { useTranslation } from "react-i18next";

type UseCloseActivityActionProps = {
  activityId: number;
  isAuditRole: boolean;
  selectedReason: ClosureReasonKey | null;
  deleteBookings: boolean;
  shortlistIds: number[];
  handleSuccess: VoidFunction;
};

export const useCloseActivityAction = ({
  activityId,
  isAuditRole,
  selectedReason,
  deleteBookings,
  shortlistIds,
  handleSuccess
}: UseCloseActivityActionProps) => {
  const { t } = useTranslation("activities");
  const { showGrowl } = useGrowlStoreActions();
  const { mutateAsync: resetShortlist } = useShortlistReset({}, { disabled: true });
  const { mutateAsync: closeActivity } = useActivityClose();
  const { invalidateAllAllocationWindows } = useTargetAllocationInvalidate();
  const { invalidateAllRoles: invalidateAllAllocationWindowRoles, invalidateAllFallbackRoles } =
    useAdminTargetAllocationInvalidate();

  const closeVacanciesAndActivity = async () => {
    try {
      if (deleteBookings) {
        await Promise.all(shortlistIds.map((shortlistId) => resetShortlist(shortlistId)));
      }
      await closeActivity({ activityId, vacanciesClosureReason: selectedReason });

      if (isAuditRole) {
        invalidateAllAllocationWindowRoles();
        invalidateAllAllocationWindows();
        invalidateAllFallbackRoles();
      }

      showGrowl({ message: t("closeActivity.success") });
      datadogRum.addAction("activity-list-close-task", { activityId });
      handleSuccess && handleSuccess();
    } catch {
      showGrowl({ message: t("closeActivity.fail"), kind: "error" });
    }
  };

  return { closeVacanciesAndActivity };
};
